import React from 'react'
// eslint-disable-next-line import/no-extraneous-dependencies
import 'react-app-polyfill/ie11'
import 'react-app-polyfill/stable'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { PublicClientApplication } from '@azure/msal-browser'
import { MsalProvider } from '@azure/msal-react'
import { Security } from '@okta/okta-react'
import { OktaAuth, toRelativeUrl } from '@okta/okta-auth-js'
import { Provider } from 'react-redux'
import { IntlProvider } from 'react-intl'
import { CustomProvider } from 'rsuite'
import frFR from 'rsuite/locales/fr_FR'
import enUS from 'rsuite/locales/en_US'
import esES from 'rsuite/locales/es_ES'
import { ConfigProvider } from './contexts/ConfigContext'
import './index.scss'
import App from './App'
import reportWebVitals from './reportWebVitals'
import store from './store'
import i18n from './i18n'
import { createMsalConfig, oktaConfig } from './views/auth/signin/authConfig'
import { loadRuntimeAuthConfig } from './views/auth/signin/authConfigLoader'

import { getLocalStorage } from './utils/localStorage'
import { AuthErrorFallback } from './components/Auth/AuthErrorFallback'

function MsalProviderWrapper({ children, msalInstanceClient, msalInstanceBo }) {
  // Déterminez quelle instance utiliser basée sur la route actuelle
  const msalInstance = window.location.pathname?.includes('/bo')
    ? msalInstanceBo
    : msalInstanceClient

  return <MsalProvider instance={msalInstance}>{children}</MsalProvider>
}

const oktaAuth = new OktaAuth({
  issuer: oktaConfig.issuer,
  clientId: oktaConfig.clientId,
  redirectUri: oktaConfig.redirectUri,
  scopes: oktaConfig.scopes,
})

const lng = getLocalStorage('LANGUAGE')

const restoreOriginalUri = async (_oktaAuth, originalUri) => {
  window.location.replace(
    toRelativeUrl(originalUri || '/', window.location.origin)
  )
}

const fallbackMsalRuntimeConfig = {
  clientId: '00000000-0000-0000-0000-000000000000',
  authority: 'https://login.microsoftonline.com/common',
  redirectUri: `${window.location.origin}/auth/oidc`,
  boRedirectUri: `${window.location.origin}/bo/auth/oidc`,
  postLogoutRedirectUri: '/',
  navigateToLoginRequestUrl: false,
}

const container = document.getElementById('root')
const root = createRoot(container)

const renderApp = async () => {
  const runtimeAuthConfig = await loadRuntimeAuthConfig()
  const msalRuntimeConfig = runtimeAuthConfig?.msal || fallbackMsalRuntimeConfig

  if (!runtimeAuthConfig) {
    // eslint-disable-next-line no-console
    console.warn(
      'auth-config.json absent: demarrage avec la configuration MSAL de secours.'
    )
  }

  const msalInstanceClient = new PublicClientApplication(
    createMsalConfig({
      ...msalRuntimeConfig,
      redirectUri: msalRuntimeConfig.redirectUri,
    })
  )
  const msalInstanceBo = new PublicClientApplication(
    createMsalConfig({
      ...msalRuntimeConfig,
      redirectUri: msalRuntimeConfig.boRedirectUri,
    })
  )

  root.render(
    <BrowserRouter>
      <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
        <MsalProviderWrapper
          msalInstanceClient={msalInstanceClient}
          msalInstanceBo={msalInstanceBo}
        >
          <Provider store={store}>
            <ConfigProvider>
              <IntlProvider locale={i18n.language}>
                <CustomProvider
                  locale={lng === 'es' ? esES : lng === 'en' ? enUS : frFR}
                >
                  <App />
                </CustomProvider>
              </IntlProvider>
            </ConfigProvider>
          </Provider>
        </MsalProviderWrapper>
      </Security>
    </BrowserRouter>
  )
}

// Styles de secours isolés et indentés

renderApp().catch((error) => {
  // eslint-disable-next-line no-console
  console.error(error)

  root.render(<AuthErrorFallback />)
})

reportWebVitals()

